import React, {useEffect, useMemo, useState} from 'react';
import {
    Button,
    DialogActions,
    IconButton,
    InputAdornment,
    Skeleton,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import AddTwoToneIcon from "@mui/icons-material/AddTwoTone";
import ContentCopyTwoToneIcon from "@mui/icons-material/ContentCopyTwoTone";
import DescriptionTwoToneIcon from "@mui/icons-material/DescriptionTwoTone";
import EditTwoToneIcon from "@mui/icons-material/EditTwoTone";
import FirstPageTwoToneIcon from "@mui/icons-material/FirstPageTwoTone";
import LastPageTwoToneIcon from "@mui/icons-material/LastPageTwoTone";
import NavigateBeforeTwoToneIcon from "@mui/icons-material/NavigateBeforeTwoTone";
import NavigateNextTwoToneIcon from "@mui/icons-material/NavigateNextTwoTone";
import SearchTwoToneIcon from "@mui/icons-material/SearchTwoTone";
import VisibilityTwoToneIcon from "@mui/icons-material/VisibilityTwoTone";
import moment from "moment";
import {getAllDynamicForm} from "../../../services/DynamicFormService";
import DynamicFormExt from "../ticket/details/DynamicFormExt";
import {useHistory} from "react-router-dom";
import {GestioneModuliStyle} from "../../../styles/pages/modules/GestioneModuliStyle";
import SimpleDialog from "../../widgets/dialogs/SimpleDialog";
import AppPage from "../../widgets/layout/AppPage";
import {UserStyle} from "../../../styles/pages/users/UserStyle";
import AutocompleteSelect from "../../widgets/inputs/AutocompleteSelect";

const actionColors = {
    preview: "#1976d2",
    duplicate: "#74b526",
    edit: "#d32f2f",
};

const dateFields = [
    {label: "Creazione", keys: ["createdAt", "dataCreazione", "creationDate", "created"]},
];

const rowsPerPageOptions = [5, 10, 20, 50].map(value => ({value, label: `${value}`}));

const getFirstValue = (item, keys) => keys.map(key => item?.[key]).find(Boolean);

const formatDate = (value) => {
    const date = moment(value);
    return date.isValid() ? date.format("DD/MM/YYYY HH:mm") : "";
};

const getTechnicalInfo = (item) => {
    const info = [
        {label: "Tipo", value: item?.type || ""},
    ];

    dateFields.forEach(({label, keys}) => {
        const value = formatDate(getFirstValue(item, keys));
        if (value) info.push({label, value});
    });

    if (item?.lastModify) {
        info.push({label: "Operatore", value: item.lastModify});
    }

    return info.filter(itemInfo => itemInfo.value);
};

const GestioneModuli = () => {
    const moduliClasses = GestioneModuliStyle();
    const classes = UserStyle();
    const history = useHistory();

    const [dynamicForm, setDynamicForm] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedConfig, setSelectedConfig] = useState(null);

    useEffect(() => {
        setLoading(true);
        getAllDynamicForm()
            .then(data => {
                setDynamicForm(data || []);
                setLoading(false);
            })
            .catch(error => {
                setLoading(false);
                window.alert(error.message);
            });
    }, []);

    const filteredForms = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        if (!term) return dynamicForm;

        return dynamicForm.filter(item => (
            item?.label?.toLowerCase().includes(term) ||
            item?.type?.toLowerCase().includes(term) ||
            `${item?.id || ""}`.includes(term)
        ));
    }, [dynamicForm, searchTerm]);

    const paginatedForms = useMemo(() => (
        filteredForms.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    ), [filteredForms, page, rowsPerPage]);

    const fieldCount = useMemo(() => (
        dynamicForm.reduce((total, item) => total + (item?.config?.length || 0), 0)
    ), [dynamicForm]);

    const totalPages = Math.max(Math.ceil(filteredForms.length / rowsPerPage), 1);
    const rangeFrom = filteredForms.length === 0 ? 0 : page * rowsPerPage + 1;
    const rangeTo = Math.min((page + 1) * rowsPerPage, filteredForms.length);

    const handleSearch = (event) => {
        setSearchTerm(event.target.value);
        setPage(0);
    };

    const handleGoToPage = (newPage) => {
        setPage(Math.min(Math.max(newPage, 0), totalPages - 1));
    };

    const handleChangeRowsPerPage = (value) => {
        setRowsPerPage(Number(value));
        setPage(0);
    };

    const handleOpenDialog = (item) => {
        setSelectedConfig(item);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedConfig(null);
    };

    const handleEdit = (item) => {
        history.push("/gestione/moduli/edit/" + item.id, item);
    };

    const handleNew = () => {
        history.push("/gestione/moduli/edit/" + 0, {config: []});
    };

    const renderSkeletonCards = () => (
        <div className={moduliClasses.moduleList}>
            {Array.from(new Array(rowsPerPage)).map((_, index) => (
                <Skeleton key={`module-skeleton-${index}`} className={moduliClasses.skeletonRow} variant="rounded"/>
            ))}
        </div>
    );

    return (
        <React.Fragment>
            <AppPage
                title="Gestione moduli"
                mainClassName={classes.page}
                containerClassName={classes.container}
            >
                <section className={classes.hero}>
                    <div>
                        <Typography className={classes.eyebrow}>Atlantis workspace</Typography>
                        <Typography component="h1" className={classes.heroTitle}>
                            Gestione moduli
                        </Typography>
                        <Typography className={classes.heroSubtitle}>
                            Crea, modifica e controlla le configurazioni dei form dinamici pubblicati nei percorsi.
                        </Typography>
                    </div>
                    <div className={classes.heroStats}>
                        <div className={classes.heroStat}>
                            <Typography className={classes.statLabel}>Moduli</Typography>
                            <Typography className={classes.statValue}>{dynamicForm.length}</Typography>
                        </div>
                        <div className={classes.heroStat}>
                            <Typography className={classes.statLabel}>Campi</Typography>
                            <Typography className={classes.statValue}>{fieldCount}</Typography>
                        </div>
                        <div className={classes.heroStat}>
                            <Typography className={classes.statLabel}>Risultati</Typography>
                            <Typography className={classes.statValue}>{filteredForms.length}</Typography>
                        </div>
                    </div>
                </section>

                <section className={classes.searchPanel}>
                    <TextField
                        autoFocus
                        className={classes.searchField}
                        fullWidth
                        onChange={handleSearch}
                        placeholder="Cerca per nome, tipo o ID modulo"
                        size="small"
                        value={searchTerm}
                        variant="outlined"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchTwoToneIcon/>
                                </InputAdornment>
                            ),
                        }}
                    />
                </section>

                <section className={classes.usersPanel}>
                    <header className={classes.usersPanelHeader}>
                        <div>
                            <Typography className={classes.panelTitle}>
                                Moduli dinamici
                            </Typography>
                            <Typography className={classes.panelSubtitle}>
                                Risultati filtrati per ricerca corrente, con anteprima rapida e modifica configurazione.
                            </Typography>
                        </div>
                        <div className={moduliClasses.panelActions}>
                            <Tooltip title="Crea modulo">
                                <IconButton
                                    aria-label="Crea modulo"
                                    className={moduliClasses.addButton}
                                    color="primary"
                                    onClick={handleNew}
                                    size="small"
                                >
                                    <AddTwoToneIcon/>
                                </IconButton>
                            </Tooltip>
                        </div>
                    </header>

                    {loading && renderSkeletonCards()}

                    {!loading && filteredForms.length === 0 && (
                        <Typography className={classes.emptyState}>
                            Nessun modulo trovato con i filtri correnti.
                        </Typography>
                    )}

                    {!loading && filteredForms.length > 0 && (
                        <div className={moduliClasses.moduleList}>
                            {paginatedForms.map((item) => {
                                const technicalInfo = getTechnicalInfo(item);

                                return (
                                    <article key={item.id} className={moduliClasses.moduleCard}>
                                        <span className={moduliClasses.moduleAvatar}>
                                            <DescriptionTwoToneIcon/>
                                        </span>
                                        <span className={moduliClasses.moduleCardBody}>
                                            <span className={moduliClasses.moduleCardTop}>
                                                <span className={moduliClasses.moduleIdBadge}>#{item.id}</span>
                                                <Typography className={classes.userAlias}>
                                                    {item.label || "Modulo senza nome"}
                                                </Typography>
                                            </span>
                                            <span className={moduliClasses.techInfoList}>
                                                {technicalInfo.map((info) => (
                                                    <span className={moduliClasses.techInfo} key={`${item.id}-${info.label}`}>
                                                        <span>{info.label}</span>
                                                        <strong>{info.value}</strong>
                                                    </span>
                                                ))}
                                            </span>
                                        </span>
                                        <span className={moduliClasses.moduleMetric}>
                                            <Typography className={moduliClasses.metricValue}>{item?.config?.length || 0}</Typography>
                                            <Typography className={moduliClasses.metricLabel}>campi</Typography>
                                        </span>
                                        <span className={moduliClasses.cardActions}>
                                            <Tooltip title="Visualizza anteprima">
                                                <IconButton
                                                    aria-label={`Visualizza anteprima ${item.label}`}
                                                    className={moduliClasses.actionButton}
                                                    onClick={() => handleOpenDialog(item)}
                                                    size="small"
                                                    style={{"--module-action-color": actionColors.preview}}
                                                >
                                                    <VisibilityTwoToneIcon/>
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Duplica modulo">
                                                <IconButton
                                                    aria-label={`Duplica ${item.label}`}
                                                    className={moduliClasses.actionButton}
                                                    onClick={() => handleEdit(item)}
                                                    size="small"
                                                    style={{"--module-action-color": actionColors.duplicate}}
                                                >
                                                    <ContentCopyTwoToneIcon/>
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Modifica modulo">
                                                <IconButton
                                                    aria-label={`Modifica ${item.label}`}
                                                    className={moduliClasses.actionButton}
                                                    onClick={() => handleEdit(item)}
                                                    size="small"
                                                    style={{"--module-action-color": actionColors.edit}}
                                                >
                                                    <EditTwoToneIcon/>
                                                </IconButton>
                                            </Tooltip>
                                        </span>
                                    </article>
                                );
                            })}
                        </div>
                    )}

                    {!loading && filteredForms.length > 0 && (
                        <div className={moduliClasses.pagination}>
                            <div className={moduliClasses.paginationBar}>
                                <span className={moduliClasses.paginationRows}>
                                    <Typography component="span">Righe</Typography>
                                    <AutocompleteSelect
                                        className={moduliClasses.paginationSelect}
                                        disableClearable
                                        onChange={handleChangeRowsPerPage}
                                        options={rowsPerPageOptions}
                                        size="small"
                                        value={rowsPerPage}
                                    />
                                </span>
                                <span className={moduliClasses.paginationActions}>
                                    <IconButton
                                        aria-label="Vai alla prima pagina"
                                        className={moduliClasses.paginationButton}
                                        disabled={page === 0}
                                        onClick={() => handleGoToPage(0)}
                                        size="small"
                                    >
                                        <FirstPageTwoToneIcon/>
                                    </IconButton>
                                    <IconButton
                                        aria-label="Pagina precedente"
                                        className={moduliClasses.paginationButton}
                                        disabled={page === 0}
                                        onClick={() => handleGoToPage(page - 1)}
                                        size="small"
                                    >
                                        <NavigateBeforeTwoToneIcon/>
                                    </IconButton>
                                    <span className={moduliClasses.paginationRange}>
                                        {rangeFrom} - {rangeTo} di {filteredForms.length}
                                    </span>
                                    <IconButton
                                        aria-label="Pagina successiva"
                                        className={moduliClasses.paginationButton}
                                        disabled={page >= totalPages - 1}
                                        onClick={() => handleGoToPage(page + 1)}
                                        size="small"
                                    >
                                        <NavigateNextTwoToneIcon/>
                                    </IconButton>
                                    <IconButton
                                        aria-label="Vai all'ultima pagina"
                                        className={moduliClasses.paginationButton}
                                        disabled={page >= totalPages - 1}
                                        onClick={() => handleGoToPage(totalPages - 1)}
                                        size="small"
                                    >
                                        <LastPageTwoToneIcon/>
                                    </IconButton>
                                </span>
                            </div>
                        </div>
                    )}
                </section>
            </AppPage>

            <SimpleDialog
                open={openDialog}
                handleClose={handleCloseDialog}
                title="Anteprima modulo"
                subtitle={selectedConfig?.label || "Controlla layout e campi prima di usare il modulo all'esterno."}
                maxWidth="md"
                showCloseButton
                closeOnBackdropClick
                content={(
                    selectedConfig && (
                        <div className={classes.dialogContent}>
                            <div className={classes.dialogSummaryGrid}>
                                <div className={classes.dialogFact}>
                                    <Typography className={classes.dialogFactLabel}>ID modulo</Typography>
                                    <strong className={classes.dialogFactValue}>#{selectedConfig.id}</strong>
                                </div>
                                {getTechnicalInfo(selectedConfig).map((info) => (
                                    <div className={classes.dialogFact} key={`dialog-${info.label}`}>
                                        <Typography className={classes.dialogFactLabel}>{info.label}</Typography>
                                        <strong className={classes.dialogFactValue}>{info.value}</strong>
                                    </div>
                                ))}
                                <div className={classes.dialogFact}>
                                    <Typography className={classes.dialogFactLabel}>Campi</Typography>
                                    <strong className={classes.dialogFactValue}>{selectedConfig?.config?.length || 0}</strong>
                                </div>
                            </div>
                            <section className={classes.dialogSection}>
                                <Typography className={classes.dialogSectionTitle}>Anteprima campi</Typography>
                                <DynamicFormExt
                                    clienteCensito={false}
                                    fields={selectedConfig.config}
                                    onDataChange={() => {}}
                                />
                            </section>
                        </div>
                    )
                )}
                actions={selectedConfig && (
                    <DialogActions className={classes.dialogActions}>
                        <Button
                            className={classes.dialogButton}
                            disableElevation
                            onClick={() => handleEdit(selectedConfig)}
                            size="small"
                            startIcon={<EditTwoToneIcon/>}
                            variant="contained"
                        >
                            Modifica modulo
                        </Button>
                    </DialogActions>
                )}
            />
        </React.Fragment>
    );
};

export default React.memo(GestioneModuli);
